import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import configurationIcon from '../../../assets/img/overviewicons/Configuration.png';
import propertyIcon from '../../../assets/img/overviewicons/property.png';
import projectStatusIcon from '../../../assets/img/overviewicons/project status.png';
import reraDetailsIcon from '../../../assets/img/overviewicons/rera details.png';
import subTypeIcon from '../../../assets/img/overviewicons/subtyp.png';
import bathroomIcon from '../../../assets/img/overviewicons/bathroom.png';
import constructionAgeIcon from '../../../assets/img/overviewicons/construction-ge.png';
import parkingIcon from '../../../assets/img/overviewicons/car-parking.png';
import SectionInfoBox from '../../../components/SectionInfoBox';
import api from '../../../utils/api';

interface ProjectOverviewProps {
    project: any;
    formatPossessionDate: (date: any) => string;
    setShowBrochureModal: (show: boolean) => void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project, formatPossessionDate, setShowBrochureModal }) => {
    const [showReraModal, setShowReraModal] = useState(false);
    const [scoreSettings, setScoreSettings] = useState<any>(null);

    useEffect(() => {
        api.getScoreSettings()
            .then(data => {
                if (data && !data.message) {
                    setScoreSettings(data);
                }
            })
            .catch(err => console.error("Error fetching score settings:", err));
    }, []);

    return (
        <div id="overview" className="card border border-gray-200 shadow-sm rounded-2xl mt-6 mb-4 overflow-hidden scroll-mt-32">
            <div className="bg-white pt-2 pb-4 md:pt-4 md:pb-6 px-4 md:px-5 border-b border-gray-100">
                <div className="flex items-center">
                    <h4 className="text-lg md:text-xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] leading-tight">Project Overview</h4>
                    <SectionInfoBox text={project.sectionInfo?.overview} />
                </div>

                {/* Redesigned Premium Icon Boxes */}
                <div className="grid grid-cols-2 md:flex md:flex-wrap items-stretch lg:flex-nowrap gap-2 md:gap-3 mt-6">
                    {/* 1. Listing Status Box */}
                    <div className="flex items-center gap-2 md:gap-3 bg-slate-50 p-2.5 md:p-3 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:shadow-xl group/box cursor-default flex-grow md:flex-none md:min-w-[140px] h-full">
                        <div className="w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-xl bg-gradient-to-br from-[#05144b] to-[#4c1d95] flex items-center justify-center shadow-lg group-hover/box:scale-110 transition-transform p-2">
                            <i className="fa-solid fa-tag text-accent text-xs md:text-sm"></i>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] md:text-[11px] font-medium text-gray-500 mb-0.5 tracking-wide">Status</p>
                            <p className="text-[#05144b] text-[11px] md:text-[13px] font-bold leading-tight">{project.listingType || 'For Sale'}</p>
                        </div>
                    </div>

                    {/* 2. Construction Progress Box */}
                    <div className="flex items-center gap-2 md:gap-3 bg-slate-50 p-2.5 md:p-3 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:shadow-xl group/box cursor-default flex-grow md:flex-none md:min-w-[140px] h-full">
                        <div className="w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg group-hover/box:scale-110 transition-transform p-2">
                            <img src={projectStatusIcon} className="w-full h-full object-contain brightness-0 invert" alt="Progress" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] md:text-[11px] font-medium text-gray-500 mb-0.5 tracking-wide">Progress</p>
                            <p className="text-[#05144b] text-[11px] md:text-[13px] font-bold leading-tight">
                                {project.progressPercentage !== undefined && project.progressPercentage !== null 
                                    ? `${project.progressPercentage}%` 
                                    : '0%'}
                            </p>
                        </div>
                    </div>

                    {/* 3. RERA Approval Box */}

                    {/* 4. Brochure Download Box */}
                    {project.brochureUrl && (
                        <div
                            onClick={() => setShowBrochureModal(true)}
                            className="flex items-center gap-2 md:gap-3 bg-red-500/10 p-2.5 md:p-3 rounded-2xl border border-red-500/20 transition-all hover:bg-white hover:shadow-xl group/box cursor-pointer flex-grow md:flex-none md:min-w-[140px] h-full"
                        >
                            <div className="w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-xl bg-red-600 flex items-center justify-center shadow-lg group-hover/box:scale-110 transition-transform p-2">
                                <i className="fa-solid fa-file-pdf text-white text-xs md:text-sm"></i>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] md:text-[11px] font-medium text-red-600 mb-0.5">Brochure</p>
                                <p className="text-red-700 text-[11px] md:text-[13px] font-bold leading-tight">Download PDF</p>
                            </div>
                        </div>
                    )}

                    {/* 5. Trust Score Box */}
                    {(() => {
                        if (scoreSettings && scoreSettings.showTrustScore === false) return null;

                        const total = (project.builderTrustScore || 0) +
                            (project.localityTrustScore || 0) +
                            (project.loadingScore || 0) +
                            (project.legalTrustScore || 0) +
                            (project.amenitiesScore || 0) +
                            (project.specificationsScore || 0) +
                            (project.constructionStatusScore || 0);

                        if (total > 0) {
                            return (
                                <div className="flex items-center gap-2 md:gap-3 bg-emerald-500/10 p-2.5 md:p-3 rounded-2xl border border-emerald-500/20 transition-all hover:bg-white hover:shadow-xl group/box cursor-default flex-grow md:flex-none md:min-w-[140px] h-full">
                                    <div className="w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-xl bg-emerald-600 flex items-center justify-center shadow-lg group-hover/box:scale-110 transition-transform p-2">
                                        <img src="/assets/img/overviewicons/trust-score.png" className="w-full h-full object-contain brightness-0 invert" alt="Trust" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] md:text-[11px] font-medium text-emerald-600 mb-0.5">Trust Score</p>
                                        <p className="text-emerald-700 text-[11px] md:text-[13px] font-bold leading-tight">{total.toFixed(1)}/100</p>
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>
            <div className="p-2 md:p-2 pb-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-1 md:gap-y-2 gap-x-2 md:gap-x-4">
                    {/* 1. Property Type (Moved to 1st) */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={propertyIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Property Type" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Property Type</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap truncate">
                                {(() => {
                                    const val = project.propertyType;
                                    if (!val) return 'Residential';

                                    const items = (Array.isArray(val) ? val : String(val).split(/[,\s]+/))
                                        .map(s => s.trim())
                                        .filter(Boolean);

                                    const uniqueItems: string[] = [];
                                    const seen = new Set<string>();

                                    for (const item of items) {
                                        const lower = item.toLowerCase();
                                        if (!seen.has(lower)) {
                                            seen.add(lower);
                                            uniqueItems.push(item);
                                        }
                                    }

                                    return uniqueItems.join(', ');
                                })()}
                            </p>
                        </div>
                    </div>

                    {/* 2. Sub Type (Moved to 2nd) */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={subTypeIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Sub Type" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Sub Type</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap truncate">
                                {Array.isArray(project.subCategory) ? project.subCategory.join(', ') : project.subCategory || 'Residential'}
                            </p>
                        </div>
                    </div>

                    {/* 3. Configuration */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={configurationIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Configuration" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Configuration</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap truncate">
                                {project.overview?.configuration ? (
                                    project.overview.configuration.toLowerCase().trim().endsWith('bhk') 
                                        ? project.overview.configuration 
                                        : `${project.overview.configuration} BHK`
                                ) : 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 4. Total Plot Area */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/total plot area.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Total Plot Area" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Total Plot Area</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.totalPlotArea || project.overview?.projectArea || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 5. Structure */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-10 h-10 md:w-16 md:h-16 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/structural.png" className="w-5 h-5 md:w-10 md:h-10 object-contain" alt="Structure" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Total Floors / Structure</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.totalFloors ? (
                                    `${project.overview.totalFloors} FL`
                                ) : project.overview?.floors || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 6. Construction Status */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/project status.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Construction Status" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Status</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap truncate">
                                {project.constructionStatus || project.projectStatus || 'Ready'}
                            </p>
                        </div>
                    </div>

                    {/* 7. Launch & Possession */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/lunch&possession.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Launch & Possession" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Possession</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {formatPossessionDate(project.possessionDate) || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 8. Price Range */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/price range.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Price Range" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Budget</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.priceRange || project.priceRange || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 9. Average Price */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/avrage price.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Average Price" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Avg. Price</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.avgPricePerSqFt || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 10. RERA Details */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/rera details.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="RERA Details" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">RERA Approved</p>
                            <div className="flex items-center gap-2">
                                <span className="text-[#05144b] text-[12px] md:text-[14px] font-black leading-none">
                                    {project.reraApproved || (project.reraNumber ? 'Yes' : 'No')}
                                </span>
                                {(project.reraApproved === 'Yes' || (!project.reraApproved && project.reraNumber)) && project.reraNumber && (
                                    <button 
                                        onClick={() => setShowReraModal(true)}
                                        className="text-[10px] md:text-[11px] text-blue-600 hover:text-blue-800 font-extrabold underline bg-transparent border-none p-0 cursor-pointer active:scale-95 transition-all inline-flex items-center"
                                        title="Click to view RERA Registration Number"
                                    >
                                        (View Number)
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 11. Total Tower & Units */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/structural.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Total Tower & Units" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Total Tower & Units</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.totalTowers || project.overview?.totalUnits ? (
                                    `${project.overview.totalTowers || '0'} T | ${project.overview.totalUnits || '0'} U`
                                ) : 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 12. Open Space */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/open space.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Open Space" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Open Space</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.openSpacePercentage || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 13. Bathrooms */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={bathroomIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Bathrooms" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Bathrooms</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.bathrooms || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* 14. Age of Construction */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={constructionAgeIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Construction Age" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Construction Age</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.ageOfConstruction || 'New'}
                            </p>
                        </div>
                    </div>

                    {/* 15. Parking */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src={parkingIcon} className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Parking" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Allotted Parking</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {project.overview?.parking || 'Available'}
                            </p>
                        </div>
                    </div>

                    {/* 16. Total Amenities */}
                    <div className="flex items-center gap-2 md:gap-4 p-1.5 md:p-3 rounded-xl hover:bg-gray-50 transition-all group">
                        <div className="w-12 h-12 md:w-18 md:h-18 shrink-0 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all duration-300">
                            <img src="/assets/img/overviewicons/amanites.png" className="w-7 h-7 md:w-12 md:h-12 object-contain" alt="Amenities" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[#05144b] text-[10px] md:text-[11px] font-medium mb-0.5 md:mb-1 whitespace-nowrap tracking-wide opacity-60">Total Amenities</p>
                            <p className="text-[#05144b] text-[12px] md:text-[14px] font-bold leading-tight whitespace-nowrap">
                                {(() => {
                                    const val = project.overview?.amenitiesIcon;
                                    if (val && !val.includes('fa-') && !val.includes('fa-solid')) {
                                        return val;
                                    }
                                    return project.amenities?.length > 0 
                                        ? `${project.amenities.length}+ Amenities` 
                                        : '25+ Amenities';
                                })()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* RERA Number Modal */}
            {showReraModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowReraModal(false)}>
                    <div 
                        className="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden transform transition-all duration-300 scale-100 p-6 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                    <i className="fa-solid fa-circle-check text-emerald-600 text-lg"></i>
                                </div>
                                <h3 className="text-base font-black text-[#05144b]">RERA Authentication</h3>
                            </div>
                            <button 
                                onClick={() => setShowReraModal(false)}
                                className="w-8 h-8 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-all cursor-pointer border-none"
                            >
                                <i className="fa-solid fa-xmark text-sm"></i>
                            </button>
                        </div>
                        
                        <div className="py-4 text-center space-y-3">
                            <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-2">
                                <img src="/assets/img/overviewicons/rera details.png" className="w-10 h-10 object-contain" alt="RERA badge" />
                            </div>
                            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Official Registration Number</p>
                            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 group hover:border-blue-200 transition-all">
                                <span className="font-mono text-sm md:text-base font-black text-[#05144b] tracking-wider select-all break-all text-center sm:text-left">
                                    {project.reraNumber}
                                </span>
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(project.reraNumber);
                                        toast.success('RERA Number copied!');
                                    }}
                                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-all cursor-pointer border-none shadow-sm shrink-0"
                                >
                                    <i className="fa-regular fa-copy"></i> Copy
                                </button>
                            </div>
                        </div>

                        {project.reraLink && (
                            <div className="pt-2 border-t border-gray-50">
                                <a 
                                    href={project.reraLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-3 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-2xl text-xs font-bold flex items-center justify-center gap-2 hover:shadow-lg active:scale-98 transition-all no-underline"
                                >
                                    <i className="fa-solid fa-arrow-up-right-from-square"></i> Visit Official RERA Portal
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectOverview;
